import type { BrowserOptions } from '@sentry/browser';
import { importDefault } from '@snapchat/import-tools';
import type {
  BasePermission,
  EmptyObject,
  SentryCapturer,
  SentryEventListenerProps,
} from '@snapchat/logging';
import { AbstractSentryEventListener } from '@snapchat/logging';

interface Props<LoggingContext extends EmptyObject, CustomEvents extends EmptyObject>
  extends BrowserOptions,
    SentryEventListenerProps<LoggingContext, CustomEvents> {}

/**
 * Error capture event listener that goes into go/sentry.
 *
 * See https://docs.sentry.io/clients/javascript/ for public API See
 * https://wiki.sc-corp.net/display/TOOL/Sentry+User+Guide for the overall guide See
 * https://github.sc-corp.net/Snapchat/web-common/tree/master/packages/sentry-browser for code
 * samples
 */

// generic pattern for matching all requests.  Used as a fallback if props does not define the requred `allowUrls` prop.
const matchAllRequests = /.*/;

export class SentryClientEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends BasePermission = BasePermission
> extends AbstractSentryEventListener<LoggingContext, CustomEvents, LoggingPermission> {
  private readonly browserProps: Props<LoggingContext, CustomEvents>;

  constructor(props: Props<LoggingContext, CustomEvents>) {
    super(props);
    this.browserProps = props;
  }

  /** @override */
  protected getExceptionCapturer = async (): Promise<SentryCapturer> => {
    const SnapSentry = importDefault(await import('@snapchat/sentry-browser'), 'Sentry');
    const { captureException, init } = SnapSentry.Sentry;

    // specify a wildcard parameter for the `allowUrls` prop if not specified via constructor
    init({ allowUrls: [matchAllRequests], ...this.browserProps });
    return captureException;
  };
}
